import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import cn from "classnames";

import { actions as homeActions } from "../../../../actions/home";
import EventStripCard from "./components/eventStripCard";
import styles from "./upcomingEventsStrip.module.css";

// The grid's track: minmax(270px, 1fr) columns with a 20px gap (upcomingEventsStrip.module.css `.grid`).
const MIN_CARD_WIDTH = 270;
const GRID_GAP = 20;
// Below this many columns the panel is a horizontal scroller instead of a grid.
const MIN_GRID_COLUMNS = 3;
const GRID_ROWS = 2;

// The heading's calendar icon, inline rather than an .svg import: decorative, and one line of markup.
const CalendarIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" strokeLinecap="round" />
  </svg>
);

/**
 * How many minmax(270px, 1fr) columns fit in `innerWidth` with a 20px gap — the same count the CSS
 * grid's auto-fill lays out, so slicing the items to columns × 2 fills exactly two rows.
 *
 * @param {number} innerWidth the panel's content-box width, px
 * @returns {number} at least 1
 */
export const gridColumns = innerWidth => Math.max(1, Math.floor((innerWidth + GRID_GAP) / (MIN_CARD_WIDTH + GRID_GAP)));

/**
 * The panel's content-box width, kept current with a ResizeObserver. Measured in a layout effect,
 * before the browser paints, so a wide screen never shows the scroller first and then jumps to the
 * two-row grid (a layout shift). 0 where ResizeObserver doesn't exist (jsdom) — which renders the
 * scroller, the layout with no width assumption.
 *
 * @param {React.RefObject} ref
 * @param {boolean} active the panel is rendered (it returns null with no items)
 * @returns {number}
 */
const useInnerWidth = (ref, active) => {
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!active || !el || typeof ResizeObserver === "undefined") return undefined;

    setWidth(el.getBoundingClientRect().width);
    const observer = new ResizeObserver(entries => {
      setWidth(entries[0].contentRect.width);
    });
    observer.observe(el);

    return () => observer.disconnect();
  }, [ref, active]);

  return width;
};

/**
 * The upcoming-events panel on My Feed (GROWTH-7131; v3 layout GROWTH-7573): up to 8 events the
 * member follows, over the next 14 days, soonest first, full width above the stream and the
 * sidebar. Hidden entirely when there is nothing to show — no empty-state card.
 *
 * Where three or more 270px columns fit, the events are a grid of exactly two rows (columns × 2
 * cards; fewer only when there are fewer events — Nima, 2026-10-17: show them all). Narrower, the
 * panel is a horizontal scroller of 300px cards with every event.
 *
 * `communityId` is the My Feed sidebar's scope (GROWTH-7130), the same value FeedStream gets:
 * the panel and the stream always show the same community. null = "All communities".
 *
 * @param {Object} props
 * @param {number|null} [props.communityId] the sidebar's selected community; null/omitted = "All communities"
 * @param {Object} [props.linkState] the router state each card's links carry (GROWTH-7576, helpers/feedReturn.js)
 * @param {string} [props.className]
 */
const UpcomingEventsStrip = ({ communityId, linkState, className }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { upcomingEvents, favoritesChangedAt } = useSelector(state => state.home);
  const { changedAt: followingChangedAt } = useSelector(state => state.following);
  const { items } = upcomingEvents;
  const bodyRef = useRef(null);
  const columns = gridColumns(useInnerWidth(bodyRef, items.length > 0));
  const isGrid = columns >= MIN_GRID_COLUMNS;
  const shown = isGrid ? items.slice(0, columns * GRID_ROWS) : items;

  // Same cache-busting convention as FeedStream: a follow or save/unsave change must not be
  // served from GET /api-v2/feed/upcoming-events's 60s browser cache.
  const fresh = Math.max(followingChangedAt || 0, favoritesChangedAt || 0) || undefined;

  useEffect(() => {
    const controller = new AbortController();
    dispatch(homeActions.getUpcomingEvents({ communityId, fresh, signal: controller.signal }));

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, communityId]);

  if (!items.length) return null;

  return (
    <section className={cn(styles.strip, className)} aria-label={t("dashboard_feed_upcoming_events_title")}>
      <div className={styles.header}>
        <h2 className={styles.heading}>
          <CalendarIcon className={styles.headingIcon} />
          {t("dashboard_feed_upcoming_events_title")}
        </h2>
        <Link to="/?type=event" className={styles.seeAll}>
          {t("dashboard_feed_upcoming_events_see_all")} <span aria-hidden="true">→</span>
        </Link>
      </div>

      <div ref={bodyRef} className={isGrid ? styles.grid : styles.scroller}>
        {shown.map(item => (
          <EventStripCard key={item.id} item={item} linkState={linkState} />
        ))}
      </div>
    </section>
  );
};

export default UpcomingEventsStrip;
